import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import Folder from 'lucide-react/dist/esm/icons/folder';
import File from 'lucide-react/dist/esm/icons/file';
import Upload from 'lucide-react/dist/esm/icons/upload';
import Search from 'lucide-react/dist/esm/icons/search';
import Grid from 'lucide-react/dist/esm/icons/grid';
import List from 'lucide-react/dist/esm/icons/list';
import MoreVertical from 'lucide-react/dist/esm/icons/more-vertical';
import Plus from 'lucide-react/dist/esm/icons/plus';
import ArrowLeft from 'lucide-react/dist/esm/icons/arrow-left';
import Home from 'lucide-react/dist/esm/icons/home';
import Trash2 from 'lucide-react/dist/esm/icons/trash-2';
import Download from 'lucide-react/dist/esm/icons/download';
import Edit from 'lucide-react/dist/esm/icons/edit';
import Share from 'lucide-react/dist/esm/icons/share';
import Tag from 'lucide-react/dist/esm/icons/tag';
import Calendar from 'lucide-react/dist/esm/icons/calendar';
import HardDrive from 'lucide-react/dist/esm/icons/hard-drive';
import BarChart3 from 'lucide-react/dist/esm/icons/bar-chart-3';
import { myFilesService, MyFile, MyFolder, FileUploadOptions } from '../../lib/myFilesService';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Breadcrumb } from '../common/Breadcrumb';

// Sub-components
import { FileUploadModal } from './FileUploadModal';
import { FolderCreateModal } from './FolderCreateModal';
import { FileDetailsModal } from './FileDetailsModal';
import { FileGrid } from './FileGrid';
import { FileList } from './FileList';
import { FolderTree } from './FolderTree';
import { FileStats } from './FileStats';

// Add DocumentPreviewModal component
function DocumentPreviewModal({ isOpen, file, onClose }: { isOpen: boolean, file: MyFile | null, onClose: () => void }) {
  const [publicUrl, setPublicUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  // Generate URL only when modal opens and file changes
  useEffect(() => {
    if (isOpen && file) {
      try {
        const { data } = supabase.storage
          .from('my-files')
          .getPublicUrl(file.file_path);
        
        setPublicUrl(data.publicUrl);
        setError(null);
      } catch (err) {
        setError('Failed to generate preview URL');
        setPublicUrl(null);
      }
    } else {
      setPublicUrl(null);
      setError(null);
    }
  }, [isOpen, file?.id]); // Only depend on file ID, not the entire file object
  
  if (!isOpen || !file) return null;
  
  const isPDF = file.mime_type?.includes('pdf') || file.file_type?.includes('pdf');
  const isImage = file.mime_type?.startsWith('image') || file.file_type?.startsWith('image');

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg max-w-3xl w-full max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">Preview: {file.title}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800">
            ×
          </button>
        </div>
        <div className="flex-1 overflow-auto flex items-center justify-center p-4">
          {error ? (
            <div className="text-red-500 text-center">
              <p>{error}</p>
              <button 
                onClick={() => window.open(publicUrl, '_blank')}
                className="mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Open in New Tab
              </button>
            </div>
          ) : !publicUrl ? (
            <div className="text-gray-500">Loading preview...</div>
          ) : isPDF ? (
            <iframe src={publicUrl} title="PDF Preview" className="w-full h-[70vh] border rounded" />
          ) : isImage ? (
            <img src={publicUrl} alt={file.title} className="max-w-full max-h-[70vh] rounded" />
          ) : (
            <div className="text-gray-500 text-center">
              <p>No preview available for this file type.</p>
              <button 
                onClick={() => window.open(publicUrl, '_blank')}
                className="mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Open in New Tab
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function MyFilesManagement() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // State management
  const [files, setFiles] = useState<MyFile[]>([]);
  const [folders, setFolders] = useState<MyFolder[]>([]);
  const [currentPath, setCurrentPath] = useState('/');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Modal states
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [showFileDetails, setShowFileDetails] = useState(false);
  const [selectedFile, setSelectedFile] = useState<MyFile | null>(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewFile, setPreviewFile] = useState<MyFile | null>(null);
  
  // Upload states
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Listen for global refresh-my-files event to reload files/folders (background only)
  useEffect(() => {
    const handler = () => {
      // Background refresh - don't show loading state to user
      loadDataInBackground();
    };
    window.addEventListener('refresh-my-files', handler);
    return () => {
      window.removeEventListener('refresh-my-files', handler);
    };
  }, [user]); // Include user so the closure is fresh when the authenticated user changes

  // Load files and folders on component mount and path change
  useEffect(() => {
    if (!user) {
      setError('User not authenticated. Please log in again.');
      return;
    }
    
    // Always load data when currentPath or selectedCategory changes
    loadData();
  }, [user, currentPath, selectedCategory]);

  // Initialize user folders on first visit
  useEffect(() => {
    const initializeUserFolders = async () => {
      if (!user) return;
      
      try {
        // Check if user has any folders
        const { data: existingFolders } = await supabase
          .from('my_folders')
          .select('id')
          .eq('user_id', user.id)
          .limit(1);
        
        // If no folders exist, create a dummy file to trigger folder initialization
        if (!existingFolders || existingFolders.length === 0) {
          console.log('Initializing user folders for first time visit');
          
          // Create a temporary file to trigger the database trigger
          const { error } = await supabase
            .from('my_files')
            .insert({
              user_id: user.id,
              title: 'Welcome to My Files',
              description: 'This is your personal file management system',
              file_path: '/welcome.txt',
              file_name: 'welcome.txt',
              file_size: 1,
              file_type: 'text/plain',
              mime_type: 'text/plain',
              folder_path: '/',
              category: 'custom',
              tags: [],
              shared_with: []
            });
          
          if (error) {
            console.error('Error initializing folders:', error);
          } else {
            // Remove the temporary file
            await supabase
              .from('my_files')
              .delete()
              .eq('user_id', user.id)
              .eq('file_name', 'welcome.txt');
          }
        }
      } catch (err) {
        console.error('Error checking/initializing folders:', err);
      }
    };
    
    initializeUserFolders();
  }, [user]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const [filesData, foldersData] = await Promise.all([
        myFilesService.getFiles(currentPath, selectedCategory || undefined),
        myFilesService.getFolders(currentPath === '/' ? undefined : currentPath)
      ]);
      
      setFiles(filesData);
      setFolders(foldersData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load files');
    } finally {
      setIsLoading(false);
    }
  };

  // Background refresh function - doesn't show loading state to user
  const loadDataInBackground = async () => {
    try {
      const [filesData, foldersData] = await Promise.all([
        myFilesService.getFiles(currentPath, selectedCategory || undefined),
        myFilesService.getFolders(currentPath === '/' ? undefined : currentPath)
      ]);
      
      setFiles(filesData);
      setFolders(foldersData);
    } catch (err) {
      console.warn('Background refresh failed:', err);
      // Don't set error state for background refreshes
    }
  };

  const handleFileUpload = async (file: File, options: FileUploadOptions) => {
    try {
      setUploading(true);
      setUploadProgress(0);
      
      const uploadedFile = await myFilesService.uploadFile(file, {
        ...options,
        folder: currentPath
      });
      
      setFiles(prev => [uploadedFile, ...prev]);
      setShowUploadModal(false);
      setUploadProgress(100);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload file');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleFolderCreate = async (name: string, options: any) => {
    try {
      const newFolder = await myFilesService.createFolder(name, {
        ...options,
        parentPath: currentPath === '/' ? undefined : currentPath
      });
      
      setFolders(prev => [...prev, newFolder]);
      setShowFolderModal(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create folder');
    }
  };

  const handleFileDelete = async (fileId: string) => {
    if (!window.confirm('Are you sure you want to delete this file? This action cannot be undone.')) {
      return;
    }
    
    try {
      await myFilesService.deleteFile(fileId);
      setFiles(prev => prev.filter(f => f.id !== fileId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete file');
    }
  };

  const handleFolderDelete = async (folderId: string) => {
    if (!window.confirm('Are you sure you want to delete this folder? This action cannot be undone.')) {
      return;
    }
    
    try {
      await myFilesService.deleteFolder(folderId);
      setFolders(prev => prev.filter(f => f.id !== folderId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete folder');
    }
  };

  const handleFileDownload = async (fileId: string) => {
    try {
      const blob = await myFilesService.downloadFile(fileId);
      const file = files.find(f => f.id === fileId);
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file?.file_name || 'download';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to download file');
    }
  };

  const handleFolderClick = (folder: MyFolder) => {
    setCurrentPath(folder.path);
  };

  const handleFileDetails = (file: MyFile) => {
    setSelectedFile(file);
    setShowFileDetails(true);
  };

  const handleFilePreview = (file: MyFile) => {
    setPreviewFile(file);
    setShowPreviewModal(true);
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      loadData();
      return;
    }
    
    try {
      setIsLoading(true);
      const searchResults = await myFilesService.searchFiles(searchQuery, selectedCategory || undefined);
      setFiles(searchResults);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Search failed');
    } finally {
      setIsLoading(false);
    }
  };

  const navigateToParent = () => {
    if (currentPath === '/') return;
    
    const pathParts = currentPath.split('/').filter(Boolean);
    pathParts.pop();
    const parentPath = pathParts.length > 0 ? `/${pathParts.join('/')}/` : '/';
    setCurrentPath(parentPath);
  };

  const getBreadcrumbItems = () => {
    if (currentPath === '/') return [{ label: 'My Files', path: '/' }];
    
    const pathParts = currentPath.split('/').filter(Boolean);
    const items = [{ label: 'My Files', path: '/' }];
    
    let currentPathBuilder = '';
    pathParts.forEach(part => {
      currentPathBuilder += `/${part}/`;
      items.push({ label: part, path: currentPathBuilder });
    });
    
    return items;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (fileType: string) => {
    if (fileType?.includes('image')) return '🖼️';
    if (fileType?.includes('pdf')) return '📄';
    if (fileType?.includes('word')) return '📝';
    if (fileType?.includes('excel') || fileType?.includes('spreadsheet')) return '📊';
    if (fileType?.includes('powerpoint') || fileType?.includes('presentation')) return '📈';
    if (fileType?.includes('video')) return '🎥';
    if (fileType?.includes('audio')) return '🎵';
    if (fileType?.includes('zip') || fileType?.includes('rar')) return '📦';
    return '📄';
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-idecide-dark">My Files</h1>
            <p className="text-gray-600">Manage all your documents and files in one place</p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={() => setShowFolderModal(true)}
              variant="outline"
              className="flex items-center gap-2"
            >
              <Folder size={16} />
              New Folder
            </Button>
            <Button
              onClick={() => setShowUploadModal(true)}
              className="flex items-center gap-2 bg-idecide-primary hover:bg-idecide-primary-hover text-idecide-dark"
            >
              <Upload size={16} />
              Upload Files
            </Button>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="flex items-center gap-2 mb-4">
          {getBreadcrumbItems().map((item, index) => (
            <React.Fragment key={item.path}>
              {index > 0 && <span className="text-gray-400">/</span>}
              <button
                onClick={() => setCurrentPath(item.path)}
                className={`text-sm hover:text-idecide-primary transition-colors ${
                  item.path === currentPath ? 'text-idecide-primary font-medium' : 'text-gray-600'
                }`}
              >
                {item.label}
              </button>
            </React.Fragment>
          ))}
        </div>

        {/* Search and Filters */}
        <div className="flex items-center gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
            <input
              type="text"
              placeholder="Search files..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-idecide-primary focus:border-transparent"
            />
          </div>
          
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-idecide-primary focus:border-transparent"
          >
            <option value="">All Categories</option>
            <option value="custom">Custom</option>
            <option value="legal">Legal</option>
            <option value="financial">Financial</option>
            <option value="health">Health</option>
            <option value="pets">Pets</option>
            <option value="travel">Travel</option>
            <option value="digital">Digital</option>
            <option value="legacy">Legacy</option>
            <option value="ndis">NDIS</option>
          </select>

          <div className="flex items-center gap-1 border border-gray-300 rounded-lg p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded ${viewMode === 'grid' ? 'bg-idecide-primary text-idecide-dark' : 'text-gray-600 hover:text-idecide-primary'}`}
            >
              <Grid size={16} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded ${viewMode === 'list' ? 'bg-idecide-primary text-idecide-dark' : 'text-gray-600 hover:text-idecide-primary'}`}
            >
              <List size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-600">{error}</p>
        </div>
      )}

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar */}
        <div className="lg:col-span-1">
          <Card className="p-4">
            <div className="mb-4">
              <h3 className="font-semibold text-idecide-dark mb-2">Quick Stats</h3>
              <FileStats />
            </div>
            
            <div className="mb-4">
              <h3 className="font-semibold text-idecide-dark mb-2">Categories</h3>
              <div className="space-y-1">
                {['custom', 'legal', 'financial', 'health', 'pets', 'travel', 'digital', 'legacy', 'ndis'].map(category => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(selectedCategory === category ? '' : category)}
                    className={`w-full text-left px-2 py-1 rounded text-sm transition-colors ${
                      selectedCategory === category 
                        ? 'bg-idecide-primary text-idecide-dark' 
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {category.charAt(0).toUpperCase() + category.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          </Card>
        </div>

        {/* Main Content Area */}
        <div className="lg:col-span-3">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-idecide-primary"></div>
            </div>
          ) : (
            <>
              {/* Folders */}
              {folders.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-idecide-dark mb-3">Folders</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {folders.map(folder => (
                      <Card
                        key={folder.id}
                        className="p-4 cursor-pointer hover:shadow-md transition-shadow"
                        onClick={() => handleFolderClick(folder)}
                      >
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-10 h-10 rounded-lg flex items-center justify-center"
                            style={{ backgroundColor: folder.color + '20' }}
                          >
                            <Folder size={20} style={{ color: folder.color }} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-idecide-dark truncate">{folder.name}</p>
                            {folder.description && (
                              <p className="text-sm text-gray-500 truncate">{folder.description}</p>
                            )}
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* Files */}
              {files.length > 0 ? (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-semibold text-idecide-dark">
                      Files ({files.length})
                    </h3>
                  </div>
                  
                  {viewMode === 'grid' ? (
                    <FileGrid
                      files={files}
                      onFileClick={handleFileDetails}
                      onFileDelete={handleFileDelete}
                      onFileDownload={handleFileDownload}
                      formatFileSize={formatFileSize}
                      getFileIcon={getFileIcon}
                      onFileShare={() => {}}
                      onFilePreview={handleFilePreview}
                    />
                  ) : (
                    <FileList
                      files={files}
                      onFileClick={handleFileDetails}
                      onFileDelete={handleFileDelete}
                      onFileDownload={handleFileDownload}
                      formatFileSize={formatFileSize}
                      getFileIcon={getFileIcon}
                    />
                  )}
                </div>
              ) : (
                <Card className="p-12 text-center">
                  <File size={48} className="mx-auto text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No files found</h3>
                  <p className="text-gray-500 mb-4">
                    {searchQuery ? 'No files match your search criteria.' : 'Upload your first file to get started.'}
                  </p>
                  <Button
                    onClick={() => setShowUploadModal(true)}
                    className="bg-idecide-primary hover:bg-idecide-primary-hover text-idecide-dark"
                  >
                    <Upload size={16} className="mr-2" />
                    Upload Files
                  </Button>
                </Card>
              )}
            </>
          )}
        </div>
      </div>

      {/* Modals */}
      <FileUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUpload={handleFileUpload}
        uploading={uploading}
        uploadProgress={uploadProgress}
        currentPath={currentPath}
      />

      <FolderCreateModal
        isOpen={showFolderModal}
        onClose={() => setShowFolderModal(false)}
        onCreate={handleFolderCreate}
        currentPath={currentPath}
      />

      <FileDetailsModal
        isOpen={showFileDetails}
        onClose={() => setShowFileDetails(false)}
        file={selectedFile}
        onDelete={handleFileDelete}
        onDownload={handleFileDownload}
        formatFileSize={formatFileSize}
        getFileIcon={getFileIcon}
      />

      {/* Preview Modal */}
      <DocumentPreviewModal isOpen={showPreviewModal} file={previewFile} onClose={() => setShowPreviewModal(false)} />
    </div>
  );
} 